import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, Flex, Text, Card, Badge } from "@radix-ui/themes";
import { Plus, Bot, MessageSquare, Settings, AlertCircle } from "lucide-react";
import { aiAgentService } from "@/services/breeze/AIAgentService";
import { DigitalOceanAgent } from "@/types/breeze/AIAgent";
import PageTemplate from "@/components/page-template/PageTemplate";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { useAuth } from "@/providers/AuthProvider";
import DropdownMenu from "@/components/ui/DropdownMenu";

// Helper function to safely convert values to strings
const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return value.toString();
  if (typeof value === "object") {
    if (value && "name" in value) return String(value.name);
    if (value && "id" in value) return String(value.id);
    return JSON.stringify(value);
  }
  return String(value);
};

export default function AIAgentsPage() {
  const { safeNavigate } = useAuth();

  const {
    data: agentsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ai-agents", "digitalocean"],
    queryFn: () => aiAgentService.getDigitalOceanAgents(),
    retry: 1,
  });

  const agents = agentsResponse?.agents || [];

  const handleCreateAgent = () => {
    safeNavigate("/ai-agents/create");
  };

  const handleEditAgent = (agent: DigitalOceanAgent) => {
    safeNavigate(`/ai-agents/${agent.id}/edit`);
  };

  const handleChatWithAgent = (agent: DigitalOceanAgent) => {
    safeNavigate(`/ai-agents/${agent.id}/chat`);
  };

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  if (error) {
    console.error("Error loading AI agents:", error);
    return (
      <PageTemplate title="AI Agents">
        <Box p="4">
          <Flex align="center" gap="2" mb="3">
            <AlertCircle size={20} color="var(--red-9)" />
            <Text color="red" weight="medium">
              Error loading AI agents
            </Text>
          </Flex>
          <Text size="2" color="gray">
            {error instanceof Error ? error.message : "Please try again later."}
          </Text>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="AI Agents"
      description="Manage and interact with your AI agents"
    >
      <Box>
        <Flex justify="between" align="center" mb="4">
          <Text size="2" color="gray">
            {agents.length} agent{agents.length !== 1 ? "s" : ""} available
          </Text>
          <Button onClick={handleCreateAgent} size="2">
            <Plus size={16} />
            Create Agent
          </Button>
        </Flex>

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          {agents.map((agent) => (
            <Card key={safeString(agent.id)} style={{ padding: "20px" }}>
              <Flex direction="column" gap="3">
                <Flex align="center" justify="between">
                  <Flex align="center" gap="2">
                    <Bot size={18} color="var(--accent-9)" />
                    <Text weight="bold" size="3">
                      {safeString(agent.name) || "Unnamed Agent"}
                    </Text>
                  </Flex>
                  <Badge color="green" variant="soft" size="1">
                    Active
                  </Badge>
                </Flex>

                {agent.description && (
                  <Text
                    size="2"
                    color="gray"
                    style={{
                      lineHeight: "1.4",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {safeString(agent.description)}
                  </Text>
                )}

                <Text size="1" color="gray">
                  Created{" "}
                  {agent.created_at
                    ? new Date(
                        safeString(agent.created_at),
                      ).toLocaleDateString()
                    : "Unknown"}
                </Text>

                <Flex gap="2" mt="1">
                  <Button
                    variant="soft"
                    size="2"
                    onClick={() => handleChatWithAgent(agent)}
                    style={{ flex: 1 }}
                  >
                    <MessageSquare size={14} />
                    Chat
                  </Button>
                  <DropdownMenu
                    items={[
                      {
                        label: "Edit",
                        icon: <Settings size={14} />,
                        onClick: () => handleEditAgent(agent),
                      },
                    ]}
                  />
                </Flex>
              </Flex>
            </Card>
          ))}
        </Box>

        {agents.length === 0 && (
          <Box
            p="6"
            style={{
              textAlign: "center",
              border: "1px dashed var(--gray-6)",
              borderRadius: "var(--radius-3)",
            }}
          >
            <Bot
              size={48}
              color="var(--gray-8)"
              style={{ margin: "0 auto 16px" }}
            />
            <Text size="3" weight="medium" mb="2">
              No AI agents yet
            </Text>
            <Text size="2" color="gray" mb="4">
              Create your first AI agent to get started
            </Text>
            <Button onClick={handleCreateAgent}>
              <Plus size={16} />
              Create Agent
            </Button>
          </Box>
        )}
      </Box>
    </PageTemplate>
  );
}
